import React, { useCallback, useEffect, useState } from 'react';
import './style.less';
import { message } from 'antd';
import { getOutboundContactCallReports } from 'shared/api';
import log from 'shared/utils/log';
import { useAppSelector } from 'shared/store';
import { Icon } from 'shared/components/Icon';
import Loader from 'shared/components/Loader';
import { Button } from 'shared/components/Button';
import { DATE_GROUPS } from 'browser/components/Dashboard/VisitorFocused/VisitorOverview';
import { OutboundCallReport } from 'types/outbound';
import { getUnixTime } from 'date-fns';
import { ConnectedCallDetailActivityItem } from './ConnectedCallDetailActivityItem';

export const ConnectedCallDetailActivity: React.FC<{ refreshKey: number }> = ({
  refreshKey,
}) => {
  const { detailTask } = useAppSelector(state => state.outbound);

  const contact = detailTask?.contact;

  const [isLoadingActivities, setIsLoadingActivities] = useState<boolean>(false);
  const [activities, setActivities] = useState<OutboundCallReport[]>([]);
  const [nextPage, setNextPage] = useState<number | undefined>(1);

  const getActivities = useCallback(async () => {
    if (!contact?.id || !nextPage) return;

    setIsLoadingActivities(true);

    try {
      const response = await getOutboundContactCallReports(contact?.id, nextPage, 10);

      setActivities([...activities, ...response.data]);

      if (response.data?.length == 10) {
        setNextPage(p => (p ?? 1) + 1);
      } else {
        setNextPage(undefined);
      }
    } catch (e) {
      log.error("Error retrieving Contacts's recent Actions.");
      message.error('There was an issue retrieving Activities.');
    }
    setIsLoadingActivities(false);
  }, [activities, contact?.id, nextPage]);

  const refetchActivities = useCallback(async () => {
    if (!contact?.id) return;
    setIsLoadingActivities(true);

    try {
      const response = await getOutboundContactCallReports(contact?.id, 1, 10);

      setActivities([...response.data]);

      if (response.data?.length == 10) {
        setNextPage(2);
      } else {
        setNextPage(undefined);
      }
    } catch (e) {
      log.error("Error retrieving Contacts's recent Actions.");
      message.error('There was an issue retrieving Activities.');
    }
    setIsLoadingActivities(false);
  }, [contact?.id]);

  useEffect(() => {
    if (!refreshKey) return;
    refetchActivities();
  }, [refetchActivities, refreshKey]);

  useEffect(() => {
    if (!contact?.id) return;
    refetchActivities();
  }, [contact?.id, refetchActivities]);

  return (
    <div className="ConnectedCallDetailActivity">
      <div className="ConnectedCallDetailActivity__header">
        <Icon icon="phoneOnOutline" />
        Activity
      </div>
      {!activities.length || !contact?.id ? (
        <div className="ConnectedCallDetailActivity__body">
          <div className="ConnectedCallDetailActivity__empty">No recent activities</div>
        </div>
      ) : (
        <div className="ConnectedCallDetailActivity__body">
          {DATE_GROUPS.map(dg => {
            const groupedActivities = activities.filter(a => {
              if (!a?.dateCreated) return false;

              const startTime = getUnixTime(new Date(a?.dateCreated)) * 1000;

              if (dg.startTime === undefined) {
                return startTime < dg.endTime;
              } else {
                return startTime < dg.endTime && startTime > dg.startTime;
              }
            });

            if (groupedActivities && groupedActivities.length > 0) {
              return (
                <>
                  <div
                    key={`sectionHeader-${dg.label}`}
                    className="ConnectedCallDetailActivity__sectionHeader"
                  >
                    {dg.label}
                  </div>
                  <div
                    key={`section-${dg.label}`}
                    className="ConnectedCallDetailActivity__section"
                  >
                    {groupedActivities.map(activity => (
                      <ConnectedCallDetailActivityItem
                        activity={activity}
                        key={activity?.dialTask?.id}
                      />
                    ))}
                  </div>
                </>
              );
            }
          })}
          {isLoadingActivities && (
            <div className="ConnectedCallDetailActivity__loader">
              {isLoadingActivities && <Loader inline={true} size="large" />}
            </div>
          )}
          {nextPage && (
            <div className="ConnectedCallDetailActivity__loader">
              <Button
                type="ghost"
                className="ConnectedCallDetailActivity__filter"
                onClick={() => getActivities()}
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
